import React, { useCallback, useState, useEffect, useMemo } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';

import AgGrid from '../../shared/agGrid/agGrid.component';
import getColumnDefinitions from './columnDefinitionsForEditView';
import ConfirmDialog from '../../shared/confirmDialog.component';
import NavigationPrompt from '../../shared/navigationPrompt.component';
import EditableGridButtons from '../../shared/gridEdit/EditableGridButtons.component';
import { OptionsPropType } from '../../shared/propTypes/common';
import { saveColumnStateToLocalStorage } from '../../shared/gridEdit/utils/gridStorageProxy';
import { usePrevious } from '../../shared/hooks/usePrevious';

import { BookToTaxDifferencePropType, CountryTaxTypePropType, BookToTaxCodeDescriptionPropType } from './propTypes';
import { useEditableGridLogic, GridMode } from '../../shared/gridEdit/useEditableGridLogic';
import { columnsToolPanel, filtersToolPanel } from '../../shared/gridEdit/toolPanels';
import DeleteBookToTaxDifferenceModal from './deleteBookToTaxDifferencesModal.component';

import { createBookToTaxDifference } from './helpers';
import { validateBookToTaxDifference } from './validators';
import {
  allCountries,
  CTT_REGEXP,
  GROUPED_COLUMN_MIN_WIDTH,
  CTT_COLUMN_DEFAULT_WIDTH,
} from './constants';

const BookToTaxDifferencesGrid = props => {
  const {
    countryTaxTypes,
    companyCodes,
    connections,
    businessUnits,
    accounts,
    isImportingBookToTaxDifferences,
    isLoadingBookToTaxDifferences,
    isLoadingInitialData,
    isBatchUpdating,
    bookToTaxDifferences,
    batchUpdateBookToTaxDifferences,
    isAdding,
    addBookToTaxDifference,
    bookToTaxCodeDescriptions,
    bookToTaxCodeEnabledFlag,
    canModify,
    deleteModalData,
    hideBookToTaxDifferenceDeleteModal,
    removeBookToTaxDifference,
    isRemovingBookToTaxDifference,
    clientId,
    periodName,
    countryCode,
  } = props;

  const [mode, setMode] = useState(GridMode.View);

  useEffect(() => {
    setMode(GridMode.View);
  }, [setMode, periodName]);

  const [isReseting, setReseting] = useState(false);
  const [isCancelDialogVisible, setIsCancelDialogVisible] = useState(false);

  const showCancelDialog = useCallback(() => setIsCancelDialogVisible(true), [setIsCancelDialogVisible]);
  const hideCancelDialog = useCallback(() => setIsCancelDialogVisible(false), [setIsCancelDialogVisible]);

  const shouldShowNavigationPrompt = mode === GridMode.EditAll || mode === GridMode.Add || mode === GridMode.Edit;

  const onSave = useCallback(
    async ({ addedRows, updatedRows, hasErrors, reset, findRowIndexBy, clearRangeSelection }) => {
      if (hasErrors) {
        return;
      }
      if (mode === GridMode.Add) {
        const bookToTaxDifferenceToAdd = addedRows[0];
        setMode(GridMode.View);
        addBookToTaxDifference(
          bookToTaxDifferenceToAdd,
          { resetEditableGrid: reset, findRowIndexBy, clearRangeSelection }
        );
        return;
      }

      if (mode === GridMode.Edit) {
        setMode(GridMode.View);
        batchUpdateBookToTaxDifferences(
          updatedRows,
          { resetEditableGrid: reset, findRowIndexBy, clearRangeSelection }
        );
        return;
      }

      if (mode === GridMode.EditAll) {
        setMode(GridMode.View);
        batchUpdateBookToTaxDifferences(updatedRows, { resetEditableGrid: reset });
      }
    },
    [mode, addBookToTaxDifference, batchUpdateBookToTaxDifferences]
  );

  const onReset = () => {
    setReseting(false);
  };

  const onCancel = (hasChanges, reset) => {
    if (hasChanges) {
      showCancelDialog();
    } else {
      setMode(GridMode.View);
      reset();
    }
  };

  const useEditableGridLogicContext = useMemo(() => ({
    countryTaxTypes,
    connections,
    businessUnits,
    bookToTaxCodeDescriptions,
    bookToTaxCodeEnabledFlag,
  }), [
    countryTaxTypes,
    connections,
    businessUnits,
    bookToTaxCodeDescriptions,
    bookToTaxCodeEnabledFlag,
  ]);

  const {
    onGridReady,
    reset,
    save,
    cancel,
    setRowData,
    stopEditing,
    addRow,
    getRowNodeId,
    processCellFromClipboard,
    editedRowId,
    setEditedRow,
    clearRangeSelection,
    gridApi,
    onSelectionChanged,
    dataHasErrors,
    ...gridProps
  } = useEditableGridLogic({
    data: bookToTaxDifferences,
    idField: 'exceptionId',
    mode,
    createRow: createBookToTaxDifference,
    validateRow: validateBookToTaxDifference,
    onSave,
    onReset,
    onCancel,
    context: useEditableGridLogicContext,
    clientId,
  });

  const editRow = useCallback(data => {
    setMode(GridMode.Edit);
    setEditedRow(data);
  }, [setEditedRow]);

  const prevCountryCode = usePrevious(countryCode);

  const context = useMemo(() => ({
    mode,
    editedRowId,
    canModify,
  }), [
    mode,
    editedRowId,
    canModify,
  ]);

  const columnDefinitions = useMemo(() => (
    isLoadingInitialData
      ? []
      : getColumnDefinitions({
        setRowData,
        editRow,
        stopEditing,

        countryTaxTypes,
        connections,
        businessUnits,
        companyCodes,
        accounts,
        bookToTaxCodeDescriptions,
        countryCode,

        context,
      })
  ), [
    isLoadingInitialData,

    setRowData,
    editRow,
    stopEditing,

    countryTaxTypes,
    connections,
    businessUnits,
    companyCodes,
    accounts,
    bookToTaxCodeDescriptions,
    countryCode,

    context,
  ]);

  useEffect(() => {
    if (gridApi && countryCode !== prevCountryCode && prevCountryCode !== null) {
      const cttColumnIds = gridApi.getColumnState()
        .filter(column => column.colId.match(CTT_REGEXP))
        .map(column => column.colId);

      if (countryCode === allCountries.countryCode) {
        gridApi.setColumnsVisible(cttColumnIds, true);
      } else {
        gridApi.setColumnsVisible(cttColumnIds.filter(colId => colId.startsWith(countryCode)), true);
        gridApi.setColumnsVisible(cttColumnIds.filter(colId => !colId.startsWith(countryCode)), false);
      }
      saveColumnStateToLocalStorage(gridApi, clientId);
    }
  }, [gridApi, countryCode, prevCountryCode, clientId]);

  const onColumnVisible = useCallback(event => {
    const { gridApi } = event.api;

    if (!gridApi || !gridApi.getAllDisplayedColumnGroups) {
      return false;
    }

    const columnGroups = gridApi.getAllDisplayedColumnGroups();

    columnGroups.forEach(group => {
      const columns = group.children;

      if (columns) {
        const cttColumns = columns
          .filter(column => column.colId.match(CTT_REGEXP) && !column.hide);

        if (cttColumns.length) {
          const width = cttColumns.length && cttColumns.length <= 3
            ? GROUPED_COLUMN_MIN_WIDTH / cttColumns.length
            : CTT_COLUMN_DEFAULT_WIDTH;

          gridApi.setColumnWidths(cttColumns.map(({ colId }) => ({ key: colId, newWidth: width })));
        }
      }
    });

    gridProps.onColumnVisible(event);
  }, [gridProps]);

  const onEditAllClick = useCallback(() => setMode(GridMode.EditAll), [setMode]);

  const onCancelClick = useCallback(() => {
    cancel();
  }, [cancel]);

  const onAddClick = useCallback(() => {
    setMode(GridMode.Add);
    addRow();
  }, [setMode, addRow]);

  const onSaveClick = useCallback(() => {
    save();
  }, [save]);

  const confirmCancel = useCallback(() => {
    setReseting(true);
    reset();
    hideCancelDialog();
    setMode(GridMode.View);
  }, [reset, hideCancelDialog, setMode, setReseting]);

  const isGridLoading = isLoadingInitialData
    || isImportingBookToTaxDifferences
    || isLoadingBookToTaxDifferences
    || isReseting
    || isBatchUpdating
    || isAdding;

  const disableGridButtons = isGridLoading
    || !canModify
    || dataHasErrors ? { defaultState: true, cancel: false } : false;

  return (
    <div className="ag-grid-full-page-with-secondary-menu with-editable-grid">
      <EditableGridButtons
        mode={mode}
        disabled={disableGridButtons}
        onAddClick={onAddClick}
        onCancelClick={onCancelClick}
        onSaveClick={onSaveClick}
        onEditAllClick={onEditAllClick}
      />
      <AgGrid
        className="ag-theme-bootstrap"
        columnDefs={columnDefinitions}
        isGridLoading={isGridLoading}
        onGridReady={onGridReady}
        getRowId={getRowNodeId}
        processCellFromClipboard={processCellFromClipboard}
        enableBrowserTooltips
        stopEditingWhenCellsLoseFocus
        suppressCellFocus
        suppressMultiRangeSelection
        enableRangeSelection
        enableFillHandle
        suppressCopyRowsToClipboard
        suppressScrollOnNewData
        sideBar={{
          toolPanels: [
            columnsToolPanel,
            filtersToolPanel,
          ],
        }}
        context={context}
        withSearchBar
        onSelectionChanged={onSelectionChanged}
        {...gridProps}
        onColumnVisible={onColumnVisible}
      />
      <div className="d-flex flex-row-reverse mt-2 page-content-container" />
      <ConfirmDialog
        onCancel={hideCancelDialog}
        onConfirm={confirmCancel}
        message="Are you sure you want to discard the changes?"
        title="Confirm Cancel"
        dismissText="No"
        submitText="Yes"
        isVisible={isCancelDialogVisible}
      />
      <NavigationPrompt show={shouldShowNavigationPrompt} />
      <ReactTooltip place="right" effect="float" />
      <DeleteBookToTaxDifferenceModal
        entityToRemove={deleteModalData.data && deleteModalData.data.entity}
        hideModal={hideBookToTaxDifferenceDeleteModal}
        removeEntity={removeBookToTaxDifference}
        rowIndex={deleteModalData.data && deleteModalData.data.rowIndex}
        isRemovingInProgress={isRemovingBookToTaxDifference}
        isVisible={deleteModalData.show}
        clearRangeSelection={clearRangeSelection}
      />
    </div>
  );
};


BookToTaxDifferencesGrid.propTypes = {
  bookToTaxDifferences: PropTypes.arrayOf(BookToTaxDifferencePropType),
  countryTaxTypes: PropTypes.arrayOf(CountryTaxTypePropType),
  companyCodes: OptionsPropType,
  connections: OptionsPropType,
  businessUnits: OptionsPropType,
  accounts: OptionsPropType,
  isLoadingBookToTaxDifferences: PropTypes.bool.isRequired,
  isLoadingInitialData: PropTypes.bool.isRequired,
  isImportingBookToTaxDifferences: PropTypes.bool.isRequired,
  batchUpdateBookToTaxDifferences: PropTypes.func.isRequired,
  isBatchUpdating: PropTypes.bool.isRequired,
  addBookToTaxDifference: PropTypes.func.isRequired,
  isAdding: PropTypes.bool.isRequired,
  bookToTaxCodeDescriptions: PropTypes.arrayOf(BookToTaxCodeDescriptionPropType),
  bookToTaxCodeEnabledFlag: PropTypes.bool.isRequired,
  canModify: PropTypes.bool.isRequired,

  hideBookToTaxDifferenceDeleteModal: PropTypes.func.isRequired,
  removeBookToTaxDifference: PropTypes.func.isRequired,
  isRemovingBookToTaxDifference: PropTypes.bool,
  deleteModalData: PropTypes.shape({
    show: PropTypes.bool,
    data: PropTypes.shape({
      rowIndex: PropTypes.number,
      entity: BookToTaxDifferencePropType,
    }),
  }),

  clientId: PropTypes.number,
  periodName: PropTypes.string.isRequired,
  countryCode: PropTypes.string,
};

export default BookToTaxDifferencesGrid;
